/**
 * Parsing and display for our own date and time fields (components/ui/DateTimeFields). Native <input type=date|time>
 * follow the browser/OS locale (10/22/2026, 10:00 PM on a US machine) and ignore the 12/24-hour preference, so the app
 * never uses them. Values stay in the same wire formats the native inputs used: yyyy-MM-dd, HH:mm, yyyy-MM-ddTHH:mm.
 */
import { DateTime } from 'luxon';
import { formatClock, type ClockPref } from '@tradeoutlet/shared';

const TZ = 'Europe/London';

/** "22:30", "2230", "930", "9.30", "10pm", "6a", "10:15 PM" → "HH:mm"; null when it isn't a time. */
export function parseTime(input: string): string | null {
  const s = input.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;
  const m = /^(\d{1,2})(?:[:.h]?(\d{2}))?(a|am|p|pm)?$/.exec(s);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  if (min > 59) return null;
  if (m[3]) {
    if (h < 1 || h > 12) return null;
    if (m[3].startsWith('p') && h !== 12) h += 12;
    if (m[3].startsWith('a') && h === 12) h = 0;
  } else if (h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** "HH:mm" shown in the person's clock: "22:30" or "10:30 PM". */
export function showTime(hhmm: string, pref: ClockPref): string {
  const t = parseTime(hhmm);
  if (!t) return hhmm;
  const [h, m] = t.split(':').map(Number);
  return formatClock(DateTime.fromObject({ hour: h, minute: m }, { zone: TZ }), pref);
}

const DATE_FORMATS = ['d/M/yyyy', 'd/M/yy', 'd.M.yyyy', 'd-M-yyyy', 'yyyy-MM-dd', 'd LLL yyyy', 'd LLLL yyyy', 'ccc d LLL yyyy', 'cccc d LLLL yyyy', 'd LLL', 'ccc d LLL', 'd/M'];

/** UK-style dates (22/10/2026, 22/10/26, 22/10, 22 Oct, Thu 22 Oct 2026, today, tomorrow, 2026-10-22) → "yyyy-MM-dd". */
export function parseDate(input: string, today: DateTime = DateTime.now().setZone(TZ)): string | null {
  const s = input.trim().replace(/\s+/g, ' ').replace(/,/g, '');
  if (!s) return null;
  const word = s.toLowerCase();
  if (word === 'today') return today.toISODate();
  if (word === 'tomorrow') return today.plus({ days: 1 }).toISODate();
  if (word === 'yesterday') return today.minus({ days: 1 }).toISODate();
  for (const f of DATE_FORMATS) {
    const d = DateTime.fromFormat(s, f, { zone: TZ, locale: 'en-GB' });
    if (d.isValid) {
      const withYear = /y/.test(f) ? d : d.set({ year: today.year });
      if (withYear.year < 1900 || withYear.year > 2200) continue;
      return withYear.toISODate();
    }
  }
  return null;
}

/** Display: "Thu 22 Oct 2026" (resting) or "22/10/2026" (while typing). */
export function showDate(iso: string, editing = false): string {
  const d = DateTime.fromISO(iso, { zone: TZ });
  if (!d.isValid) return iso;
  return d.setLocale('en-GB').toFormat(editing ? 'dd/MM/yyyy' : 'ccc d LLL yyyy');
}

export function addMinutes(hhmm: string, mins: number): string {
  const t = parseTime(hhmm) ?? '00:00';
  const [h, m] = t.split(':').map(Number);
  const total = (((h * 60 + m + mins) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export const addDays = (iso: string, days: number) => DateTime.fromISO(iso, { zone: TZ }).plus({ days }).toISODate()!;

/** Within the native-style min/max bounds (inclusive, same string formats). */
export const inRange = (v: string, min?: string, max?: string) => (!min || v >= min) && (!max || v <= max);
